import React, {
  ChangeEvent,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Placement } from '@floating-ui/react';
import { Props as InputProps } from '../../Input/Input';
import { ComboBoxListItem } from '../components/ComboBoxListItem';
import { ComboBoxList } from '../components/ComboBoxList';
import { useComboBox } from '../components/useComboBox';
import { filterComboBoxData } from '../components/comboBoxFiltering';
import styles from './ComboBox.module.scss';
import { Icon, IconList, Input, VisuallyHidden } from 'components/core';
import { Stack } from 'components/layout';

// What decides the highlighted option: the text typed since the list was last
// reset (the first option containing it), or where the user moved the
// highlight with the arrow keys or pointer (`null` when they moved off the
// list). Resolved against the data of every render, so data that follows
// `value` is always up to date
type Highlight =
  | { kind: 'typed'; text: string }
  | { kind: 'navigated'; index: number | null }
  | null;

const resolveHighlight = (
  highlight: Highlight,
  list: string[]
): number | null => {
  if (highlight === null) return null;
  if (highlight.kind === 'navigated') return highlight.index;

  const index = list.findIndex((item) =>
    item.toLowerCase().includes(highlight.text.toLowerCase())
  );
  return index === -1 ? null : index;
};

export type ComboBoxProps = {
  data?: string[];
  value: string;
  listHeading?: string;
  placement?: Placement;
  noResultsMessage?: string;
  /**
   * Fired when the user commits a value, always with the committed string. By
   * default this means an explicit selection — clicking an option, pressing
   * Enter on a highlighted option, or typing a name that matches an option
   * exactly (case-insensitive) — or clearing the field (an empty value).
   * Partial, non-matching text typed into the input is NOT propagated, so
   * consumers never receive a value that isn't a real selection. Set
   * `allowFreeText` to also receive every keystroke.
   */
  onChange: (value: string) => void;
  hasChevron?: boolean;
  enableFiltering?: boolean;
  /**
   * Treat any typed text as a valid value. When set, `onChange` fires on every
   * keystroke (the legacy behaviour) so consumers can accept free-form input
   * that isn't one of `data`'s options. Leave off for select-from-list inputs.
   */
  allowFreeText?: boolean;
  // Omit Input's native onChange so the committed-string signature above wins
  // over the inherited ChangeEvent handler.
} & Omit<InputProps, 'onChange'>;

const ComboBox = (
  {
    data = [],
    value,
    placeholder,
    placement,
    readOnly,
    listHeading,
    noResultsMessage = 'No matching results',
    hasChevron = true,
    enableFiltering = false,
    allowFreeText = false,
    onFocus,
    onChange,
    onBlur,
    onKeyDown,
    ...rest
  }: ComboBoxProps,
  passedInRef: ForwardedRef<HTMLInputElement>
) => {
  const [isListOpen, setIsListOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlight, setHighlight] = useState<Highlight>(null);
  const listRef = useRef<(HTMLElement | null)[]>([]);

  const displayedData = enableFiltering
    ? filterComboBoxData(data, inputValue, value)
    : data;
  const shouldShowNoResultsMessage =
    enableFiltering && inputValue.trim() !== '' && displayedData.length === 0;
  const hasPopupContent =
    Boolean(displayedData.length) || shouldShowNoResultsMessage;

  // A list with nothing to show is not shown: no empty popup under the input,
  // and no focus manager hiding the rest of the page from screen readers (or
  // catching Tab) while the user types into a ComboBox that has no options
  const isListShown = isListOpen && hasPopupContent;
  const activeListItemIndex = resolveHighlight(highlight, displayedData);

  const {
    nodeId,
    context,
    refs,
    floatingStyles,
    mergedReferenceRefs,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  } = useComboBox({
    isOpen: isListShown,
    setIsOpen: setIsListOpen,
    placement,
    listRef,
    activeIndex: activeListItemIndex,
    setActiveIndex: (index) => setHighlight({ kind: 'navigated', index }),
    forwardedRef: passedInRef,
  });

  useEffect(() => {
    return () => setIsListOpen(false);
  }, []);

  // Keep the displayed input in sync with the externally-tracked `value`.
  // Consumers update `value` after commits (e.g. an async save) and expect the
  // input to reflect the latest persisted state.
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Commit a value to the consumer. Reserved for explicit selections (option
  // click / Enter on a matching option) and clears — these are the only
  // gestures that propagate via `onChange` unless `allowFreeText` is set.
  const commitValue = (value: string) => {
    setInputValue(value);
    onChange(value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    // If focus is moving into the floating list (the user clicked an option),
    // swallow the blur entirely. The user hasn't actually left the combobox
    // interaction — the option's onClick will commit a new value and refocus
    // the input. Bubbling the blur up would fire RHF validation and trigger
    // consumer-level "user blurred" handlers prematurely; reverting inputValue
    // would re-render the list unfiltered and yank the option out from under
    // the cursor.
    const next = event.relatedTarget as Node | null;
    const floatingEl = refs.floating.current;
    if (floatingEl && next && floatingEl.contains(next)) {
      return;
    }

    // With nothing to show there is no list to move into, so leaving the
    // input also withdraws the wish to show one: content arriving later (e.g.
    // async data) must not open it on its own. An open list is closed by its
    // own focus-out handling once focus has settled, which keeps Tab able to
    // move into the options
    if (!isListShown) {
      setIsListOpen(false);
    }

    // If the user typed text that the parent didn't commit (e.g. non-matching
    // input, or a selection the parent rejected) revert the displayed value to
    // the canonical `value`. Leave empty input alone so consumers can treat
    // "clear and blur" as an explicit intent.
    if (inputValue !== '' && inputValue !== value) {
      setInputValue(value);
    }
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (!readOnly && !isListOpen) {
      setIsListOpen(true);
    }

    if (isListOpen) {
      setIsListOpen(false);
    }

    // With filtering enabled, pre-select the existing text so typing replaces
    // the current value rather than appending to it — this pairs with
    // filter-as-you-type. Left off by default so free-text consumers (e.g.
    // in-place editors) keep the caret where the user clicked.
    if (enableFiltering) {
      event.currentTarget.select();
    }

    if (onFocus) {
      onFocus(event);
    }
  };

  // Returns true when Enter was used: it picked an option or closed the list
  const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const highlightedOption =
      activeListItemIndex === null
        ? undefined
        : displayedData[activeListItemIndex];

    // autocomplete + commit the highlighted option
    if (highlightedOption) {
      event.preventDefault();
      setHighlight(null);
      setIsListOpen(false);
      commitValue(highlightedOption);
      return true;
    }

    // Free-text inputs may have no matching option to commit, but Enter should
    // still dismiss the list and accept what the user typed. While the list is
    // showing, closing it is all that Enter does; the next Enter, with the list
    // closed, goes to the consumer (e.g. to move focus to the next field), so
    // focus never leaves an open list
    if (allowFreeText) {
      setHighlight(null);
      setIsListOpen(false);
      if (isListShown) {
        event.preventDefault();
        return true;
      }
    }

    return false;
  };

  // Keys the ComboBox uses itself are kept; every other one goes to
  // `onKeyDown`
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Enter':
        if (handleEnter(event)) return;
        break;
      case 'Escape':
        // useDismiss closes the list
        if (isListShown) return;
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        // useListNavigation moves the highlight and re-opens a dismissed list
        if (displayedData.length > 0) return;
        break;
    }

    onKeyDown?.(event);
  };

  const handleChange = (newValue: string) => {
    // Always reflect what the user types in the input, but only propagate a
    // committed value to the consumer. A value commits when the field is
    // cleared (empty), in free-text mode (every keystroke), or when the typed
    // text matches an option exactly (case-insensitive) — the last lets users
    // auto-select by typing a full name without pressing Enter. Partial,
    // non-matching text stays local until they pick an option or clear.
    setInputValue(newValue);
    if (newValue === '' || allowFreeText) {
      onChange(newValue);
    } else {
      const exactMatch = data.find(
        (item) => item.toLowerCase() === newValue.toLowerCase()
      );
      if (exactMatch) {
        onChange(exactMatch);
      }
    }

    if (newValue) {
      setIsListOpen(true);
      setHighlight({ kind: 'typed', text: newValue });
    } else {
      setIsListOpen(false);
      setHighlight(null);
    }
  };

  return (
    <>
      <div className={styles.inputWrapper}>
        <Input
          {...rest}
          ref={mergedReferenceRefs}
          {...getReferenceProps({
            // Prevent Chrome from adding it's own autocomplete popup
            autoComplete: 'off',
            readOnly,
            placeholder,
            value: inputValue,
            onFocus: handleFocus,
            onBlur: handleBlur,
            onChange: (e: ChangeEvent<HTMLInputElement>) =>
              handleChange(e.currentTarget.value),
            onKeyDown: handleKeyDown,
          })}
        />
        {hasChevron && (
          <Icon
            className={styles.inputChevron}
            icon={IconList.solid.faAngleDown}
          />
        )}
        <VisuallyHidden component="div" role="status">
          {shouldShowNoResultsMessage ? noResultsMessage : ''}
        </VisuallyHidden>
      </div>

      <ComboBoxList
        nodeId={nodeId}
        heading={listHeading}
        hasListItems={Boolean(displayedData.length)}
        hasPopupContent={hasPopupContent}
        context={context}
        floatingRef={refs.setFloating}
        floatingStyles={floatingStyles}
        floatingProps={getFloatingProps()}
      >
        <Stack>
          {shouldShowNoResultsMessage && (
            <div className={styles.emptyStateMessage} aria-hidden="true">
              {noResultsMessage}
            </div>
          )}
          {displayedData.map((item, index) => (
            <ComboBoxListItem
              key={item}
              {...getItemProps({
                ref(node) {
                  listRef.current[index] = node;
                },
                onMouseDown(event) {
                  // Keep focus on the input while an option is clicked. On
                  // Safari/Firefox (macOS) a <button> isn't focused on
                  // mousedown, so the input's blur would fire with a null
                  // relatedTarget — handleBlur couldn't tell an option click
                  // from the user leaving the field, would revert inputValue,
                  // and the list would re-render unfiltered, shifting the
                  // option out from under the cursor before the click landed.
                  // Preventing the default mousedown keeps focus on the input,
                  // so no blur fires at all; the relatedTarget check in
                  // handleBlur remains as a backstop.
                  event.preventDefault();
                },
                onClick() {
                  // A click is an explicit selection — commit it (firing
                  // onChange) and close the list, regardless of allowFreeText.
                  commitValue(item);
                  setHighlight(null);
                  setIsListOpen(false);

                  const inputElement = refs.domReference.current;

                  if (inputElement instanceof HTMLInputElement) {
                    // Move users cursor back to the input
                    inputElement.focus();
                  }
                },
              })}
              isActive={activeListItemIndex === index}
            >
              {item}
            </ComboBoxListItem>
          ))}
        </Stack>
      </ComboBoxList>
    </>
  );
};

const forwardedComboBox = forwardRef<HTMLInputElement, ComboBoxProps>(ComboBox);
export { forwardedComboBox as ComboBox };
